/**
 * Output Controller — output display, error handling, and auto-run orchestration.
 *
 * Owns: output/execution store selectors, output type detection, auto-copy
 * effect, auto-run pipeline effect (300ms debounce), error message computation,
 * toast notifications, output visualizer overlay, and OutputPane rendering.
 *
 * Note: auto-run depends on `input` and `pipeline` — viz mode switches mutate
 * pipeline, so switching visualizers with auto-run enabled will re-execute.
 * This is intentional existing behavior.
 */

import React, { useCallback,useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useEffectiveVisualizerMode } from '@/hooks/useEffectiveVisualizerMode';
import { useToolCatalog } from '@/hooks/useToolCatalog';
import { useTypeDetection } from '@/hooks/useTypeDetection';
import { copyToClipboard } from '@/lib/utils';
import { isEditable as isManifestEditable } from '@/lib/visualizerManifest';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';
import { useExecutionStore } from '@/store/useExecutionStore';
import { usePreferencesStore } from '@/store/usePreferencesStore';
import { useToastStore } from '@/store/useToastStore';
import type { VisualizerMode } from '@/store/useVisualizerStore';

import { OutputPane, type OutputView } from './OutputPane';
import { PanelVisualizer } from './PanelVisualizer';
import { useVisualizerPanel } from './VisualizerManager';

export const OutputController: React.FC = () => {
  const { t } = useTranslation('editor');
  const catalog = useToolCatalog();
  const { runPipeline, rerunFromStep } = useAppStore();
  const input = useEditorStore((s) => s.input);
  const output = useEditorStore((s) => s.output);
  const pipeline = useEditorStore((s) => s.pipeline);
  const stepThrough = useExecutionStore((s) => s.stepThrough);
  const isProcessing = useExecutionStore((s) => s.isProcessing);
  const errors = useExecutionStore((s) => s.errors);
  const rerunFromStepIndex = useExecutionStore((s) => s.rerunFromStepIndex);

  const autoRunPipeline = usePreferencesStore((s) => s.autoRunPipeline);
  const setAutoRunPipeline = usePreferencesStore((s) => s.setAutoRunPipeline);
  const autoCopyOutputToClipboard = usePreferencesStore((s) => s.autoCopyOutputToClipboard);
  const setAutoCopyOutputToClipboard = usePreferencesStore((s) => s.setAutoCopyOutputToClipboard);

  const { type: outputDetectedType } = useTypeDetection(output, 200);

  const [outputView, setOutputView] = useState<OutputView>('text');
  const [fullscreen, setFullscreen] = useState(false);

  const handleCopy = async () => { await copyToClipboard(output); };

  // Auto-copy output to clipboard when it changes
  const lastAutoCopiedOutputRef = useRef<string>('');
  useEffect(() => {
    if (!autoCopyOutputToClipboard) return;
    if (!output) return;
    if (output === lastAutoCopiedOutputRef.current) return;
    if (document.visibilityState !== 'visible') return;
    if (typeof document.hasFocus === 'function' && !document.hasFocus()) return;
    void (async () => {
      const success = await copyToClipboard(output);
      if (success) { lastAutoCopiedOutputRef.current = output; }
    })();
  }, [autoCopyOutputToClipboard, output]);

  // Auto-run pipeline on input/config change with 300ms debounce
  useEffect(() => {
    if (!autoRunPipeline) return;
    if (pipeline.length === 0) return;
    if (stepThrough?.active) return;
    if (useExecutionStore.getState().consumeAutoRunSuppression()) return;
    const timeout = setTimeout(() => {
      if (useExecutionStore.getState().isProcessing) return;
      void runPipeline();
    }, 300);
    return () => clearTimeout(timeout);
  }, [autoRunPipeline, input, pipeline, stepThrough?.active]);

  // Error messages for the output pane banner
  const errorMessages = useMemo(() => {
    return Object.entries(errors).map(([key, msg]) => {
      if (key === '_pipeline') return msg;
      const step = pipeline.find((s) => s.id === key);
      const tool = step ? catalog.getToolById(step.toolId) : null;
      return `${tool?.name ?? key}: ${msg}`;
    });
  }, [errors, pipeline]);

  // Toast notification when new errors appear
  const prevErrorCountRef = useRef(0);
  useEffect(() => {
    const errorCount = Object.keys(errors).length;
    if (errorCount > 0 && errorCount !== prevErrorCountRef.current) {
      const firstError = Object.values(errors)[0];
      setTimeout(() => {
        useToastStore.getState().addToast(
          firstError.length > 80 ? firstError.slice(0, 80) + '…' : firstError,
          'error',
        );
      });
    }
    prevErrorCountRef.current = errorCount;
  }, [errors]);

  const viz = useVisualizerPanel('output', outputDetectedType);
  const { effectiveMode, pinnedStepId } = useEffectiveVisualizerMode('output');

  const editStepIndex = useMemo(() => {
    if (!pinnedStepId) return null;
    const index = pipeline.findIndex((step) => step.id === pinnedStepId);
    return index >= 0 ? index : null;
  }, [pipeline, pinnedStepId]);

  const canEdit = editStepIndex != null && isManifestEditable(effectiveMode);

  const handleVisualizerEdit = useCallback((newData: string) => {
    if (editStepIndex == null) return;
    void rerunFromStep(editStepIndex, newData);
  }, [editStepIndex, rerunFromStep]);

  // Output visualizer overlay
  const visualizerOverlay = useMemo(() => {
    if (viz.storeMode === 'raw') return undefined;
    return (
      <ErrorBoundary fallback={<div className="p-4 text-destructive text-sm">{t('visualizer.crashed')}</div>}>
        <PanelVisualizer
          panelRole="output"
          content={output}
          semanticType={outputDetectedType}
          onEdit={canEdit ? handleVisualizerEdit : undefined}
          fullscreen={fullscreen}
          onExitFullscreen={() => setFullscreen(false)}
          className="flex-1 min-h-0"
          quickSwitchActiveModes={viz.activeModes}
          quickSwitchCurrentMode={viz.storeMode}
          onQuickSwitchMode={(mode) => viz.switchMode(mode)}
          onQuickSwitchRemove={(mode) => viz.removeMode(mode)}
          quickSwitchAvailableModes={viz.availableModes as VisualizerMode[]}
          onQuickSwitchAdd={(mode) => { viz.addActiveMode('output', mode); viz.switchMode(mode); }}
        />
      </ErrorBoundary>
    );
  }, [output, outputDetectedType, fullscreen, viz.storeMode, viz.activeModes, viz.availableModes, viz.switchMode, viz.removeMode, viz.addActiveMode]);

  return (
    <OutputPane
      output={output}
      errorMessages={errorMessages}
      hasVisualizers={false}
      visualizerTabCount={0}
      outputView={outputView}
      onOutputViewChange={setOutputView}
      autoRunPipeline={autoRunPipeline}
      onAutoRunPipelineChange={setAutoRunPipeline}
      autoCopyOutputToClipboard={autoCopyOutputToClipboard}
      onAutoCopyOutputToClipboardChange={setAutoCopyOutputToClipboard}
      onCopy={handleCopy}
      onRun={runPipeline}
      isProcessing={isProcessing}
      isStepThroughActive={Boolean(stepThrough?.active)}
      rerunFromStepIndex={rerunFromStepIndex}
      stats={null} /* SKELETON: IO stats — wire useIOStats hook when ready */
      visualizerOverlay={visualizerOverlay}
      detectedTypeName={outputDetectedType?.semanticType}
      effectiveModeName={viz.effectiveModeName}
      canAddVisualizer={viz.canAdd}
      isExplicitVisualizerActive={viz.isExplicitActive}
      onAddVisualizerStep={viz.switchMode}
      onRemoveVisualizerStep={viz.resetMode}
      isFullscreen={fullscreen}
      onToggleFullscreen={() => setFullscreen((f) => !f)}
      quickSwitchActiveModes={viz.activeModes}
      quickSwitchCurrentMode={viz.storeMode}
      onQuickSwitchMode={(mode) => viz.switchMode(mode)}
      onQuickSwitchRemove={(mode) => viz.removeMode(mode)}
      quickSwitchAvailableModes={viz.availableModes as VisualizerMode[]}
      onQuickSwitchAdd={(mode) => { viz.addActiveMode('output', mode); viz.switchMode(mode); }}
    />
  );
};
